import { useState, useMemo } from "react";
import { EPccCommentType } from "@/constants/enums/comment-type.enum";



interface Comment {
    id?: string;
    comment?: string;
    actorName?: string;
    [key: string]: unknown;
    timestamp?: string;
    commentType?: EPccCommentType;
}

interface CommentsTrailProps {
    comments?: Comment[] | { data?: Comment[]; comments?: Comment[] };
    maxVisible?: number;
}

const getInitials = (name?: string): string => {
    if (!name || name.trim() === "") return "U";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const getAuthorName = (comment: Comment): string => {
    if (comment.commentType === EPccCommentType.APPROVER) return "Pencom Admin";
    if (comment.commentType === EPccCommentType.FIRST_REVIEWER) return "Pencom Admin";
    if (comment.commentType === EPccCommentType.SECOND_REVIEWER) return "Pencom Admin";
    return comment.actorName ?? "";
};

const getCommentText = (comment: Comment): string => {
    return comment.comment ?? "";
};

const CommentsTrail = ({ comments, maxVisible = 3 }: CommentsTrailProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const processedComments = useMemo(() => {
        if (!comments) return [];

        // Handle different response structures
        let commentsArray: Comment[] = [];
        if (Array.isArray(comments)) {
            commentsArray = comments;
        } else if (comments.data && Array.isArray(comments.data)) {
            commentsArray = comments.data;
        } else if (comments.comments && Array.isArray(comments.comments)) {
            commentsArray = comments.comments;
        }

        // Sort by date (latest first) - assuming created_at or timestamp exists
        return commentsArray.sort((a, b) => {
            const dateA = a.timestamp ?? "";
            const dateB = b.timestamp ?? "";
            return dateB.localeCompare(dateA);
        });
    }, [comments]);

    if (processedComments.length === 0) {
        return null;
    }

    const visibleComments = isExpanded
        ? processedComments
        : processedComments.slice(0, maxVisible);

    const hasMore = processedComments.length > maxVisible;

    return (
        <div className="flex flex-col gap-y-4 border border-stroke rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between">
                <p className="font-semibold text-sm font-clash-display text-medium-dark-grey">
                    Comments Trail ({processedComments.length})
                </p>
                {hasMore && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-xs font-medium font-clash-display text-primary-button hover:opacity-70 transition-opacity"
                    >
                        {isExpanded ? "Show Less" : `Show All (${processedComments.length})`}
                    </button>
                )}
            </div>

            <div className="flex flex-col gap-y-4">
                {visibleComments.map((comment, index) => {
                    const isEmployer = comment.commentType === EPccCommentType.EMPLOYER
                    const authorName = getAuthorName(comment);
                    const commentText = getCommentText(comment);
                    const initials = getInitials(authorName);

                    return (
                        <div
                            key={comment.id || index}
                            className={`flex flex-col gap-y-2 ${isEmployer ? "items-end" : "items-start"}`}
                        >
                            <div className={`flex flex-row items-start gap-x-3 ${isEmployer ? "flex-row-reverse" : ""}`}>
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-xs font-clash-display ${isEmployer
                                            ? "bg-[#E7F6EC] text-[#0F973D]"
                                            : "bg-[#E3F2FD] text-[#1976D2]"
                                            }`}
                                    >
                                        {initials}
                                    </div>
                                </div>

                                {/* Comment Content */}
                                <div
                                    className={`max-w-[80%] flex flex-col gap-y-1 rounded-2xl px-4 py-3 ${isEmployer
                                        ? "bg-[#E7F6EC] text-[#0F973D]"
                                        : "bg-[#F2F6FF] text-[#1F3C88]"
                                        }`}
                                >
                                    <p className="font-semibold text-sm font-clash-display">
                                        {authorName}
                                    </p>
                                    <p className="font-regular text-sm font-clash-display leading-relaxed text-medium-dark-grey">
                                        {commentText}
                                    </p>
                                    {(comment.timestamp) && (
                                        <p className="font-regular text-xs font-clash-display text-medium-dark-grey opacity-70 mt-1">
                                            {new Date(comment.timestamp ?? "").toLocaleDateString("en-US", {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit"
                                            })}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CommentsTrail;

